/**
 * UserWay Website Accessibility widget (app embed, config/settings_data.json)
 * — US-market restriction, hover-scale removal and hover-colour swap.
 *
 * All three read data-market-country off <body> — rendered by
 * layout/theme.liquid from localization.country.iso_code, the same
 * attribute assets/analytics-country.js reads for the same reason: it is
 * the one source of truth for the shopper's resolved market already
 * present on the page.
 *
 * ── Market restriction ──────────────────────────────────────────────────
 * UserWay has no country/market targeting of its own — only manual
 * position/appearance settings and an on/off icon toggle on the instance
 * `userway:init_completed` carries. Confirmed against UserWay's own docs
 * (userway.org/docs) before writing this: that toggle is the full extent of
 * the client-side API, so gating by market has to happen here, in the
 * theme. Hooking the init event, not polling for the instance, means the
 * icon is hidden before its first paint on any market but the US, rather
 * than flashing on and then off.
 *
 * ── Hover scale / colour (US market only) ───────────────────────────────
 * UserWay writes the icon's background as an inline
 * `style="background: rgb(...) !important"` from its own widget script
 * (the "widget_color" tuning value, confirmed against the live rendered
 * markup, TASK-54329). No stylesheet rule can beat that — an inline
 * `!important` declaration always outranks a stylesheet's `!important` for
 * the same property, whatever the selector's specificity — so the only way
 * to change what paints on hover is to overwrite that same inline
 * declaration at the same priority, which is what bind() does below: on
 * mouseenter/mouseleave for genuine hover, corrected back to the dashboard
 * default by a short poll whenever the pointer isn't over the icon (see
 * bind()'s own comment for why it polls rather than watches for UserWay's
 * menu opening/closing).
 *
 * The hover scale is also removed: src/css/theme.css carries an id-selector
 * `#userwayAccessibilityIcon:hover { transform: none !important }`, which
 * wins IF UserWay's own hover-scale rule is no more specific and loads no
 * later in the cascade — true today, but not something this theme controls
 * going forward (UserWay ships its widget CSS from its own CDN, versioned
 * independently of this repo). Setting the same inline transform here makes
 * the removal certain rather than contingent on that race — and the
 * standalone `scale` property is reset here too, not just in that CSS rule,
 * since `transform` and `scale` compose independently and the compiled CSS's
 * own `scale: 1 !important` companion gets merged away by the minifier (see
 * bindHover()'s own comment).
 */
(function () {
  'use strict';

  var CURRENT_MARKET = ((document.body && document.body.getAttribute('data-market-country')) || '').toUpperCase();
  var HOVER_BG = 'var(--color-brand-secondary-blue)';

  function hideIcon(event) {
    var instance = event.detail && event.detail.userWayInstance;
    if (instance && typeof instance.iconVisibilityOff === 'function') {
      instance.iconVisibilityOff();
    }
  }

  function bindHover(icon) {
    if (icon.__uwHoverBound) return;
    icon.__uwHoverBound = true;
    icon.style.setProperty('transform', 'none', 'important');
    icon.style.setProperty('scale', '1', 'important');
    var restBg = icon.style.background;
    var hovering = false;

    function setNormal() {
      if (!restBg) restBg = icon.style.background;
      if (restBg) icon.style.setProperty('background', restBg, 'important');
    }

    icon.addEventListener('mouseenter', function () {
      hovering = true;
      if (!restBg) restBg = icon.style.background;
      icon.style.setProperty('background', HOVER_BG, 'important');
    });
    icon.addEventListener('mouseleave', function () {
      hovering = false;
      setNormal();
    });

    /* Clicking the icon is what opens (or closes) UserWay's menu, and the
       pointer usually doesn't move off the icon when that happens — so
       mouseleave alone won't fire and the poll below skips correction while
       still "hovering". Resetting directly on click ties the colour to the
       actual state-changing action instead of waiting on pointer movement or
       the 250ms poll. */
    icon.addEventListener('click', function () {
      hovering = false;
      setNormal();
    });

    setInterval(function () {
      if (hovering || !restBg) return;
      if (icon.style.background !== restBg) {
        icon.style.setProperty('background', restBg, 'important');
      }
    }, 250);
  }

  document.addEventListener('userway:init_completed', function (event) {
    if (CURRENT_MARKET !== 'US') {
      hideIcon(event);
      return;
    }
    var icon = document.getElementById('userwayAccessibilityIcon');
    if (icon) bindHover(icon);
  });
})();
